/**
 * Commissions Manager (admin) — Monday-style sales rep + commissions tracker.
 *
 * Two tabs: Reps (CRUD profiles) + Commissions (line items with workflow).
 * Auto-created when a supplier_order flips to "Invoiced" with a sales_rep_id.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, DollarSign, Plus, Pencil, Trash2, X,
  FileText, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CHIP = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  void: "bg-slate-100 text-slate-600 border-slate-300",
};
const STATUS_LABEL = { pending: "Pending", approved: "Approved", paid: "Paid", void: "Void" };
const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const CommissionsManager = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [tab, setTab] = useState("commissions");
  const [reps, setReps] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterRep, setFilterRep] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showRepModal, setShowRepModal] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [showCommModal, setShowCommModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);

  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/api/admin/sales-reps`, { headers }),
        axios.get(`${API}/api/admin/commissions`, { headers }),
        axios.get(`${API}/api/admin/commissions/summary`, { headers }),
      ]);
      setReps(r1.data.reps || []);
      setCommissions(r2.data.commissions || []);
      setSummary(r3.data);
    } catch (e) {
      toast.error("Failed to load commissions data");
    }
  };
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line

  const filtered = useMemo(() => commissions.filter((c) => {
    if (filterRep !== "all" && c.rep_id !== filterRep) return false;
    if (filterStatus !== "all" && c.status !== filterStatus) return false;
    return true;
  }), [commissions, filterRep, filterStatus]);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/api/admin/commissions/${id}/approve`, {}, { headers });
      toast.success("Commission approved");
      load();
    } catch (e) { toast.error("Failed to approve"); }
  };
  const pay = async (id) => {
    try {
      await axios.post(`${API}/api/admin/commissions/${id}/pay`, {}, { headers });
      toast.success("Commission marked paid");
      load();
    } catch (e) { toast.error("Failed to mark paid"); }
  };
  const delComm = async (id) => {
    if (!window.confirm("Delete this commission?")) return;
    await axios.delete(`${API}/api/admin/commissions/${id}`, { headers });
    toast.success("Deleted");
    load();
  };
  const delRep = async (rep) => {
    if (!window.confirm(`Delete sales rep ${rep.name}? Their commissions remain (orphaned).`)) return;
    await axios.delete(`${API}/api/admin/sales-reps/${rep.id}`, { headers });
    toast.success("Rep deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="commissions-manager">
      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-1">
        <button
          onClick={() => setTab("commissions")}
          className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === "commissions" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"}`}
          data-testid="tab-commissions"
        >
          <DollarSign className="w-4 h-4 inline mr-1" /> Commissions
        </button>
        <button
          onClick={() => setTab("reps")}
          className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === "reps" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"}`}
          data-testid="tab-reps"
        >
          <Users className="w-4 h-4 inline mr-1" /> Sales Reps & Brokers
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryBox color="amber" label="Pending" value={fmtMoney(summary.by_status.pending.total)} sub={`${summary.by_status.pending.count} lines`} />
          <SummaryBox color="blue" label="Approved" value={fmtMoney(summary.by_status.approved.total)} sub={`${summary.by_status.approved.count} lines`} />
          <SummaryBox color="emerald" label="Paid YTD" value={fmtMoney(summary.by_status.paid.total)} sub={`${summary.by_status.paid.count} lines`} />
          <SummaryBox color="slate" label="Total Gross Profit" value={fmtMoney(summary.overall.gross_profit_total)} sub="All commissionable" />
        </div>
      )}

      {tab === "commissions" && (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
            <select value={filterRep} onChange={(e) => setFilterRep(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="filter-rep">
              <option value="all">All reps</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="filter-status">
              <option value="all">All statuses</option>
              {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <div className="ml-auto" />
            <Button
              onClick={() => setShowStatementModal(true)}
              variant="outline"
              className="gap-2"
              data-testid="rep-statement-btn"
            >
              <FileText className="w-4 h-4" /> Rep Statement (PDF)
            </Button>
            <Button onClick={() => setShowCommModal(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-commission-btn">
              <Plus className="w-4 h-4" /> Manual Entry
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="commissions-table">
              <thead className="bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">PO</th>
                  <th className="px-3 py-2 text-left">Rep</th>
                  <th className="px-3 py-2 text-left">Hospital</th>
                  <th className="px-3 py-2 text-right">Invoice</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-right">Ship</th>
                  <th className="px-3 py-2 text-right">Gross Profit</th>
                  <th className="px-3 py-2 text-right">%</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-8 text-slate-400">No commissions yet. Flip a supplier order to "Invoiced" with a rep assigned to auto-generate.</td></tr>
                ) : filtered.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`commission-row-${c.po_number || c.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{c.po_number || "—"}</td>
                    <td className="px-3 py-2 font-medium">{c.rep_name}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-xs truncate" title={c.hospital_name}>{c.hospital_name || "—"}</td>
                    <td className="px-3 py-2 text-right">{fmtMoney(c.hospital_invoice_amount)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{fmtMoney(c.supplier_cost)}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      {(!c.shipping_cost_type || c.shipping_cost_type === "free") ? (
                        <span className="text-emerald-600">Free</span>
                      ) : c.shipping_cost_type === "third_party" ? (
                        <span className="text-blue-600">3rd-party</span>
                      ) : (
                        <span className="text-slate-700">{fmtMoney(c.shipping_cost)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(c.gross_profit)}</td>
                    <td className="px-3 py-2 text-right">{c.commission_pct}%</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmtMoney(c.commission_amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_CHIP[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        {c.status === "pending" && (
                          <Button size="sm" onClick={() => approve(c.id)} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" data-testid={`approve-${c.po_number}`}>Approve</Button>
                        )}
                        {c.status === "approved" && (
                          <Button size="sm" onClick={() => pay(c.id)} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`pay-${c.po_number}`}>Mark Paid</Button>
                        )}
                        <button onClick={() => delComm(c.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`del-comm-${c.po_number}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "reps" && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => { setEditingRep(null); setShowRepModal(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-rep-btn">
              <Plus className="w-4 h-4" /> Add Sales Rep / Broker
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {reps.length === 0 && <p className="text-slate-400 italic col-span-full text-center py-8">No sales reps yet — click "Add Sales Rep" to get started.</p>}
            {reps.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`rep-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900">{r.name}</h3>
                    <p className="text-xs text-slate-500">{r.role_label} · {r.default_commission_pct}% default</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingRep(r); setShowRepModal(true); }} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`edit-rep-${r.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => delRep(r)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`del-rep-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-2">{r.email}</p>
                {r.phone && <p className="text-xs text-slate-500">{r.phone}</p>}
                <div className="grid grid-cols-3 gap-1 mt-3 pt-3 border-t border-slate-100 text-center">
                  <div><div className="text-amber-600 font-bold text-sm">{fmtMoney(r.totals?.pending || 0)}</div><div className="text-[10px] text-slate-500">Pending</div></div>
                  <div><div className="text-blue-600 font-bold text-sm">{fmtMoney(r.totals?.approved || 0)}</div><div className="text-[10px] text-slate-500">Approved</div></div>
                  <div><div className="text-emerald-600 font-bold text-sm">{fmtMoney(r.totals?.paid || 0)}</div><div className="text-[10px] text-slate-500">Paid</div></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showRepModal && (
        <RepModal headers={headers} editing={editingRep} onClose={() => { setShowRepModal(false); setEditingRep(null); }} onSaved={() => { setShowRepModal(false); setEditingRep(null); load(); }} />
      )}
      {showCommModal && (
        <CommissionModal headers={headers} reps={reps} onClose={() => setShowCommModal(false)} onSaved={() => { setShowCommModal(false); load(); }} />
      )}
      {showStatementModal && (
        <StatementModal
          headers={headers}
          reps={reps}
          onClose={() => setShowStatementModal(false)}
        />
      )}
    </div>
  );
};

const SummaryBox = ({ color, label, value, sub }) => {
  const cls = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-lg p-4 ${cls}`}>
      <div className="text-xs uppercase tracking-wide font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-xs opacity-70">{sub}</div>
    </div>
  );
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

const RepModal = ({ headers, editing, onClose, onSaved }) => {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    name: editing?.name || "",
    email: editing?.email || "",
    phone: editing?.phone || "",
    role_label: editing?.role_label || "Sales Rep",
    default_commission_pct: editing?.default_commission_pct ?? 15,
    notes: editing?.notes || "",
    is_active: editing?.is_active ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/api/admin/sales-reps/${editing.id}`, form, { headers });
      } else {
        await axios.post(`${API}/api/admin/sales-reps`, form, { headers });
      }
      toast.success(isEdit ? "Rep updated" : "Rep created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rep");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="rep-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">{isEdit ? "Edit Rep" : "New Sales Rep / Broker"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <Field label="Name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="rep-name-input" /></Field>
          <Field label="Email *"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="rep-email-input" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></Field>
          <Field label="Role"><select value={form.role_label} onChange={(e) => set("role_label", e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            {["Sales Rep", "Broker", "Account Executive", "Inside Sales", "1099 Contractor"].map((r) => <option key={r}>{r}</option>)}
          </select></Field>
          <Field label="Default Commission %"><input type="number" step="0.5" value={form.default_commission_pct} onChange={(e) => set("default_commission_pct", Number(e.target.value))} className="w-full border rounded px-3 py-2 text-sm" data-testid="rep-pct-input" /></Field>
          <Field label="Active"><select value={form.is_active ? "yes" : "no"} onChange={(e) => set("is_active", e.target.value === "yes")} className="w-full border rounded px-3 py-2 text-sm">
            <option value="yes">Active</option>
            <option value="no">Inactive</option>
          </select></Field>
          <div className="col-span-2"><Field label="Notes"><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full border rounded px-3 py-2 text-sm" /></Field></div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-rep-btn">{saving ? "Saving..." : "Save"}</Button>
        </div>
      </div>
    </div>
  );
};

const CommissionModal = ({ headers, reps, onClose, onSaved }) => {
  const [form, setForm] = useState({
    rep_id: reps[0]?.id || "",
    po_number: "",
    supplier_name: "",
    hospital_name: "",
    description: "",
    hospital_invoice_amount: "",
    supplier_cost: "",
    shipping_cost_type: "free",
    shipping_cost: 0,
    commission_pct: reps[0]?.default_commission_pct || 15,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const shipNum = form.shipping_cost_type === "actual" ? (Number(form.shipping_cost) || 0) : 0;
  const margin = Math.max((Number(form.hospital_invoice_amount) || 0) - (Number(form.supplier_cost) || 0) - shipNum, 0);
  const amount = (margin * (Number(form.commission_pct) || 0)) / 100;

  const submit = async () => {
    if (!form.rep_id || !form.hospital_invoice_amount || !form.supplier_cost) {
      toast.error("Rep, invoice and cost are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/commissions`, {
        ...form,
        hospital_invoice_amount: Number(form.hospital_invoice_amount),
        supplier_cost: Number(form.supplier_cost),
        shipping_cost: shipNum,
        shipping_cost_type: form.shipping_cost_type,
        commission_pct: Number(form.commission_pct),
      }, { headers });
      toast.success("Commission created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="comm-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg">Manual Commission Entry</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Sales Rep *">
            <select value={form.rep_id} onChange={(e) => {
              const rep = reps.find((r) => r.id === e.target.value);
              set("rep_id", e.target.value);
              if (rep) set("commission_pct", rep.default_commission_pct);
            }} className="w-full border rounded px-3 py-2 text-sm" data-testid="comm-rep-select">
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.default_commission_pct}%)</option>)}
            </select>
          </Field></div>
          <Field label="PO #"><input value={form.po_number} onChange={(e) => set("po_number", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></Field>
          <Field label="Supplier"><input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></Field>
          <div className="col-span-2"><Field label="Hospital"><input value={form.hospital_name} onChange={(e) => set("hospital_name", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></Field></div>
          <div className="col-span-2"><Field label="Description"><input value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></Field></div>
          <Field label="Hospital Invoice $ *"><input type="number" step="0.01" value={form.hospital_invoice_amount} onChange={(e) => set("hospital_invoice_amount", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="comm-invoice-input" /></Field>
          <Field label="Supplier Cost $ *"><input type="number" step="0.01" value={form.supplier_cost} onChange={(e) => set("supplier_cost", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="comm-cost-input" /></Field>
          <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-3" data-testid="comm-shipping-section">
            <div className="text-[10px] uppercase tracking-wide font-bold text-amber-900 mb-1">Shipping — never commissionable</div>
            <div className="flex flex-wrap gap-3 text-sm">
              {[
                { v: "free", l: "Free ($0)" },
                { v: "third_party", l: "Third-Party ($0 to us)" },
                { v: "actual", l: "Actual Cost" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="comm-ship-type"
                    value={opt.v}
                    checked={form.shipping_cost_type === opt.v}
                    onChange={(e) => {
                      set("shipping_cost_type", e.target.value);
                      if (e.target.value !== "actual") set("shipping_cost", 0);
                    }}
                    data-testid={`comm-ship-${opt.v}`}
                  />
                  {opt.l}
                </label>
              ))}
            </div>
            {form.shipping_cost_type === "actual" && (
              <input
                type="number" step="0.01"
                value={form.shipping_cost}
                onChange={(e) => set("shipping_cost", e.target.value)}
                placeholder="Shipping $"
                className="mt-2 w-40 border border-slate-300 rounded px-3 py-1.5 text-sm"
                data-testid="comm-shipping-input"
              />
            )}
          </div>
          <Field label="Commission %"><input type="number" step="0.5" value={form.commission_pct} onChange={(e) => set("commission_pct", e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="comm-pct-input" /></Field>
          <div className="col-span-2 bg-emerald-50 rounded p-3 text-sm" data-testid="comm-preview">
            Gross Profit (Invoice − Cost − Shipping): <strong>{fmtMoney(margin)}</strong> &nbsp;·&nbsp; Commission: <strong className="text-emerald-700">{fmtMoney(amount)}</strong>
          </div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-commission-btn">Create</Button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Rep Commission Statement modal — monthly PDF (Paid only by default)
// =============================================================================
const StatementModal = ({ headers, reps, onClose }) => {
  const today = new Date();
  const [repId, setRepId] = useState(reps[0]?.id || "");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [statusFilter, setStatusFilter] = useState("paid");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const monthNames = ["", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const doPreview = async () => {
    if (!repId) {
      toast.error("Pick a rep first");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/commissions/statement/preview`, {
        headers,
        params: { rep_id: repId, year, month, status: statusFilter },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!repId) {
      toast.error("Pick a rep first");
      return;
    }
    try {
      const url = `${API}/api/admin/commissions/statement/pdf?rep_id=${repId}&year=${year}&month=${month}&status=${statusFilter}`;
      const r = await fetch(url, { headers });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "PDF generation failed");
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (r.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/)?.[1] || "commission_statement.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const emailRep = () => {
    if (!preview?.rep?.email) {
      toast.error("Rep has no email on file");
      return;
    }
    const period = `${monthNames[month]} ${year}`;
    const t = preview.totals || {};
    const rows = preview.rows || [];
    const lines = [
      `Hi ${preview.rep.name?.split(" ")[0] || preview.rep.name || "team"},`,
      "",
      `Attached / below is your SterileMate Commission Statement for ${period} (${statusFilter} orders only).`,
      "",
      "Summary:",
      `  • Lines: ${t.rows || 0}`,
      `  • Hospital Invoices: $${(t.invoice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `  • Supplier Cost: $${(t.cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `  • Shipping (deducted, not commissionable): $${(t.shipping || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `  • Gross Profit: $${(t.gross_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `  • Commission Total: $${(t.commission || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      "",
      "Detail:",
      ...rows.map((r) => `  • PO ${r.po_number || "—"} · ${r.hospital_name || ""} · GP $${(r.gross_profit || 0).toFixed(2)} · ${r.commission_pct}% = $${(r.commission_amount || 0).toFixed(2)}`),
      "",
      "PDF copy will be sent separately. Reply with any questions.",
      "",
      "Kind regards,",
      "George Broughton III",
      "CEO · SterileMate LLC",
      "(281) 456-3458 | george.broughton@sterilemate.com",
    ];
    const subject = `SterileMate Commission Statement — ${period}`;
    const body = lines.join("\n");
    const url = `mailto:${encodeURIComponent(preview.rep.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="statement-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" /> Generate Rep Commission Statement
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900">
            Pick a sales rep + month. Default filter = <strong>Paid orders only</strong> (hospital paid us → rep can be paid).
            Generates a 1-page PDF and a pre-filled email draft for the rep.
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Sales Rep *">
              <select value={repId} onChange={(e) => { setRepId(e.target.value); setPreview(null); }} className="w-full border rounded px-3 py-2 text-sm" data-testid="statement-rep-select">
                <option value="">— pick —</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Year">
              <select value={year} onChange={(e) => { setYear(Number(e.target.value)); setPreview(null); }} className="w-full border rounded px-3 py-2 text-sm" data-testid="statement-year">
                {[year - 2, year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field label="Month">
              <select value={month} onChange={(e) => { setMonth(Number(e.target.value)); setPreview(null); }} className="w-full border rounded px-3 py-2 text-sm" data-testid="statement-month">
                {monthNames.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPreview(null); }} className="w-full border rounded px-3 py-2 text-sm" data-testid="statement-status">
                <option value="paid">Paid only</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
              </select>
            </Field>
          </div>

          <div className="flex gap-2">
            <Button onClick={doPreview} disabled={loading || !repId} variant="outline" data-testid="statement-preview-btn">
              {loading ? "Loading..." : "Preview"}
            </Button>
            <Button onClick={downloadPdf} disabled={!repId} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="statement-pdf-btn">
              <Download className="w-4 h-4" /> Download PDF
            </Button>
            <Button onClick={emailRep} disabled={!preview} variant="outline" className="gap-2" data-testid="statement-email-btn">
              Email Rep
            </Button>
          </div>

          {preview && (
            <div className="border border-slate-200 rounded p-3 space-y-2" data-testid="statement-preview">
              <div className="text-sm">
                <strong>{preview.rep?.name}</strong> · {monthNames[month]} {year} · {statusFilter} orders
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                <Stat label="Lines" v={preview.totals.rows} />
                <Stat label="Invoice" v={fmtMoney(preview.totals.invoice)} />
                <Stat label="Cost" v={fmtMoney(preview.totals.cost)} />
                <Stat label="Ship (excluded)" v={fmtMoney(preview.totals.shipping)} />
                <Stat label="Commission" v={fmtMoney(preview.totals.commission)} highlight />
              </div>
              <div className="max-h-48 overflow-y-auto border-t border-slate-200 mt-2">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left">PO</th>
                      <th className="px-2 py-1 text-left">Hospital</th>
                      <th className="px-2 py-1 text-right">GP</th>
                      <th className="px-2 py-1 text-right">%</th>
                      <th className="px-2 py-1 text-right">$ Comm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-3 text-slate-400 italic">No commissions matched.</td></tr>
                    )}
                    {preview.rows.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono">{r.po_number || "—"}</td>
                        <td className="px-2 py-1">{r.hospital_name || "—"}</td>
                        <td className="px-2 py-1 text-right">{fmtMoney(r.gross_profit)}</td>
                        <td className="px-2 py-1 text-right">{r.commission_pct}%</td>
                        <td className="px-2 py-1 text-right font-bold text-emerald-700">{fmtMoney(r.commission_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, v, highlight }) => (
  <div className={`rounded p-2 ${highlight ? "bg-emerald-100 text-emerald-800" : "bg-slate-50 text-slate-700"}`}>
    <div className="text-[10px] uppercase tracking-wide font-bold opacity-70">{label}</div>
    <div className="text-sm font-bold">{v}</div>
  </div>
);

export default CommissionsManager;